<template>
  <div class="ticket-details-page">
    <!-- Header Section -->
    <AppHeader 
      header-text="Drivers in your state can save up to $600/year"
      button-text="FREE CALL"
    />

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="navigation">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Previous Question
        </button>
      </div>
    </div>

    <!-- Ticket Details Section -->
    <section class="ticket-details-section">
      <div class="container">
        <div class="ticket-details-content">
          <PageTitle title="Ticket Details" />
          
          <div class="ticket-form">
            <!-- Tickets List -->
            <div v-for="(ticket, index) in tickets" :key="index" class="ticket-item">
              <div class="ticket-row">
                <!-- Month Dropdown -->
                <div class="dropdown-group">
                  <select 
                    v-model="ticket.month" 
                    class="ticket-dropdown"
                    :class="{ 'error': ticket.showMonthError }"
                    @change="validateTicket(index)"
                  >
                    <option value="">Month</option>
                    <option v-for="month in months" :key="month.value" :value="month.value">
                      {{ month.label }}
                    </option>
                  </select>
                  <div v-if="ticket.showMonthError" class="error-message">Required</div>
                </div>

                <!-- Year Dropdown -->
                <div class="dropdown-group">
                  <select 
                    v-model="ticket.year" 
                    class="ticket-dropdown"
                    :class="{ 'error': ticket.showYearError }"
                    @change="validateTicket(index)"
                  >
                    <option value="">Year</option>
                    <option v-for="year in availableYears" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                  <div v-if="ticket.showYearError" class="error-message">Required</div>
                </div>
              </div>

              <!-- Ticket Description Dropdown with Search -->
              <div class="dropdown-group full-width">
                <div class="searchable-dropdown" ref="dropdownRefs">
                  <input
                    type="text"
                    v-model="ticket.description"
                    @input="filterDescriptions(index)"
                    @focus="showDropdown(index)"
                    @blur="hideDropdown(index)"
                    placeholder="Ticket Description"
                    class="ticket-input"
                    :class="{ 'error': ticket.showDescriptionError }"
                  />
                  <div 
                    v-if="ticket.showDropdown" 
                    class="dropdown-list"
                  >
                    <div
                      v-for="description in ticket.filteredDescriptions"
                      :key="description"
                      @mousedown="selectDescription(index, description)"
                      class="dropdown-item"
                    >
                      {{ description }}
                    </div>
                  </div>
                </div>
                <div v-if="ticket.showDescriptionError" class="error-message">Required</div>
              </div>

              <!-- Remove Ticket Button (if more than one ticket) -->
              <div v-if="tickets.length > 1" class="remove-ticket">
                <button @click="removeTicket(index)" class="remove-btn">
                  ✕ Remove Ticket
                </button>
              </div>
            </div>

            <!-- Add Another Ticket Button -->
            <div class="add-ticket-section">
              <button @click="addTicket" class="add-ticket-btn">
                + Add another ticket
              </button>
            </div>

            <!-- Continue Button -->
            <div class="continue-section">
              <button 
                class="continue-btn"
                @click="continueToNext"
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Links -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import PageTitle from '@/components/PageTitle.vue'
import { submitDataAndLog } from '@/utils/localStorage'

export default {
  name: 'TicketDetailsPage',
  components: {
    AppHeader,
    AppFooter,
    PageTitle
  },
  data() {
    return {
      userQuoteData: null,
      tickets: [
        {
          month: '',
          year: '',
          description: '',
          showDropdown: false,
          filteredDescriptions: [],
          showMonthError: false,
          showYearError: false,
          showDescriptionError: false
        }
      ],
      months: [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
      ],
      availableYears: [],
      ticketDescriptions: [
        'Careless Driving',
        'Carpool Lane Violation',
        'Child Not In Car Seat',
        'Defective Equipment',
        'Defective Vehicle Reduced Violation',
        'Driving Too Fast For Conditions',
        'Driving Without A license',
        'Excessive Noise',
        'Exhibition Driving',
        'Expired Drivers License',
        'Expired Emissions',
        'Expired Registration',
        'Failure To Obey Traffic Signal',
        'Failure To Signal',
        'Failure To Stop',
        'Failuer To Yield',
        'Following Too CLose',
        'Illegal Lane Change',
        'Illegal Passing',
        'Illegal Turn',
        'illegal Turn On Red',
        'Illegal U Turn',
        'Inattentive Driving',
        'No Helmet',
        'No Insurance',
        'No Seatbelt',
        'Other',
        'Passing A School Bus',
        'Passing In A No Passing Zone',
        'Passing On a Shoulder',
        'Ran A Red Light',
        'Ran A Stop Sign',
        'Speeding',
        'Speeding Less Than 10 MPH Over',
        'Speeding More Than 10 MPH Over',
        'Speeding More Than 20 MPH Over',
        'Wrong Way On A One Way'
      ]
    }
  },
  mounted() {
    this.loadUserData()
    this.generateYears()
  },
  methods: {
    loadUserData() {
      const storedData = localStorage.getItem('quoteData')
      if (storedData) {
        try {
          this.userQuoteData = JSON.parse(storedData)
          // 检查是否有选择的事故信息，且有任何Yes
          if (!this.userQuoteData.incidents) {
            this.$router.push('/incidents')
            return
          }
          
          // 检查incidents数组格式：["0","0","0"] -> [accident, ticket, dui]
          // 任何一个是"1"就表示有Yes，应该显示TicketDetails页面
          const hasAnyYes = Array.isArray(this.userQuoteData.incidents) 
            ? this.userQuoteData.incidents.some(incident => incident === '1')
            : false
          
          if (!hasAnyYes) {
            // 如果都是"0"（No），应该跳转到Name页面
            console.log('TicketDetails: No incidents found, redirecting to /name')
            this.$router.push('/name')
            return
          }
          
          console.log('TicketDetails: Found incidents, staying on page:', this.userQuoteData.incidents)
          
          // 加载之前输入的罚单信息（如果有的话）
          if (this.userQuoteData.ticket_details && this.userQuoteData.ticket_details.length > 0) {
            this.tickets = this.userQuoteData.ticket_details.map(ticket => ({
              ...ticket,
              showDropdown: false,
              filteredDescriptions: [...this.ticketDescriptions],
              showMonthError: false,
              showYearError: false,
              showDescriptionError: false
            }))
          } else {
            // 初始化第一个罚单的下拉选项
            this.tickets[0].filteredDescriptions = [...this.ticketDescriptions]
          }
        } catch (error) {
          console.error('Error parsing stored data:', error)
          this.$router.push('/')
        }
      } else {
        this.$router.push('/')
      }
    },
    
    saveUserData() {
      localStorage.setItem('quoteData', JSON.stringify(this.userQuoteData))
    },
    
    generateYears() {
      // 生成当前年份和过去3年（总共4年）
      const currentYear = new Date().getFullYear()
      this.availableYears = []
      
      for (let i = 0; i < 4; i++) {
        this.availableYears.push(currentYear - i)
      }
    },
    
    isValidDescription(description) {
      return this.ticketDescriptions.includes(description)
    },
    
    filterDescriptions(index) {
      const ticket = this.tickets[index]
      const searchTerm = ticket.description.toLowerCase()
      
      if (searchTerm === '') {
        ticket.filteredDescriptions = [...this.ticketDescriptions]
      } else {
        ticket.filteredDescriptions = this.ticketDescriptions.filter(desc =>
          desc.toLowerCase().includes(searchTerm)
        )
      }
      
      ticket.showDropdown = true
    },
    
    showDropdown(index) {
      const ticket = this.tickets[index]
      if (ticket.filteredDescriptions.length === 0) {
        ticket.filteredDescriptions = [...this.ticketDescriptions]
      }
      ticket.showDropdown = true
    },
    
    hideDropdown(index) {
      // 延迟隐藏，以便处理点击事件
      setTimeout(() => {
        this.tickets[index].showDropdown = false
      }, 200)
    },
    
    selectDescription(index, description) {
      const ticket = this.tickets[index]
      ticket.description = description
      ticket.showDropdown = false
      this.validateTicket(index)
    },
    
    validateTicket(index) {
      const ticket = this.tickets[index]
      
      // 清除错误状态 - 当用户选择了有效值时
      if (ticket.month && ticket.month !== '') {
        ticket.showMonthError = false
      }
      if (ticket.year && ticket.year !== '') {
        ticket.showYearError = false
      }
      if (ticket.description && ticket.description !== '' && this.isValidDescription(ticket.description)) {
        ticket.showDescriptionError = false
      }
      
      // 如果输入的描述不在列表中，清空它
      if (ticket.description && !this.isValidDescription(ticket.description)) {
        ticket.description = ''
        ticket.showDescriptionError = true
      }
      
      this.saveTickets()
    },
    
    addTicket() {
      this.tickets.push({
        month: '',
        year: '',
        description: '',
        showDropdown: false,
        filteredDescriptions: [...this.ticketDescriptions],
        showMonthError: false,
        showYearError: false,
        showDescriptionError: false
      })
    },
    
    removeTicket(index) {
      if (this.tickets.length > 1) {
        this.tickets.splice(index, 1)
        this.saveTickets()
      }
    },
    
    saveTickets() {
      // 保存罚单数据（去掉UI相关的属性）
      const ticketsData = this.tickets.map(ticket => ({
        month: ticket.month,
        year: ticket.year,
        description: ticket.description
      }))
      
      this.userQuoteData.ticket_details = ticketsData
      this.saveUserData()
    },
    
    async continueToNext() {
      console.log('CONTINUE button clicked')
      console.log('Current tickets:', this.tickets)
      
      // 验证所有罚单
      let hasErrors = false
      
      this.tickets.forEach((ticket, index) => {
        console.log(`Validating ticket ${index}:`, ticket)
        
        // 检查月份 - 必须选择一个值
        if (!ticket.month || ticket.month === '') {
          console.log(`Ticket ${index}: Month is empty`)
          ticket.showMonthError = true
          hasErrors = true
        } else {
          ticket.showMonthError = false
        }
        
        // 检查年份 - 必须选择一个值
        if (!ticket.year || ticket.year === '') {
          console.log(`Ticket ${index}: Year is empty`)
          ticket.showYearError = true
          hasErrors = true
        } else {
          ticket.showYearError = false
        }
        
        // 检查描述 - 必须选择一个有效的描述
        if (!ticket.description || ticket.description === '' || !this.isValidDescription(ticket.description)) {
          console.log(`Ticket ${index}: Description is empty or invalid`)
          ticket.showDescriptionError = true
          hasErrors = true
        } else {
          ticket.showDescriptionError = false
        }
      })
      
      // 如果有错误，不继续执行，显示错误提示
      if (hasErrors) {
        console.log('Validation failed - showing error messages')
        console.log('Updated tickets with errors:', this.tickets)
        return
      }
      
      // 所有验证通过，保存数据
      this.saveTickets()
      
      console.log('All tickets validated successfully:', this.tickets)
      
      // 提交数据并记录页面提交事件
      await submitDataAndLog('ticketdetails')
      // 跳转到Name页面
      this.$router.push('/name')
    },
    
    goBack() {
      this.$router.push('/incidents')
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.ticket-details-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Navigation */
.navigation {
  padding: 1rem 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #999;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: #666;
}

/* Ticket Details Section */
.ticket-details-section {
  padding: 2rem 0 4rem;
  background: #fff;
  flex: 1;
}

.ticket-details-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.ticket-form {
  margin-top: 2rem;
}

.ticket-item {
  margin-bottom: 2.5rem;
  padding: 0;
  background: transparent;
}

.ticket-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.dropdown-group {
  position: relative;
}

.dropdown-group.full-width {
  grid-column: 1 / -1;
  margin-top: 1rem;
}

.ticket-dropdown, .ticket-input {
  width: 100%;
  padding: 1rem 0;
  border: none;
  border-bottom: 2px solid #e9ecef;
  border-radius: 0;
  background: transparent;
  font-size: 1rem;
  color: #333;
  transition: border-color 0.3s ease;
  outline: none;
}

.ticket-dropdown:focus, .ticket-input:focus {
  border-bottom-color: #007bff;
}

.ticket-dropdown.error, .ticket-input.error {
  border-bottom-color: #dc3545;
}

.ticket-dropdown option {
  padding: 0.5rem;
  background: #fff;
  color: #333;
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  text-align: left;
}

/* Searchable Dropdown */
.searchable-dropdown {
  position: relative;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 0.5rem;
}

.dropdown-item {
  padding: 0.8rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #e9ecef;
  text-align: left;
}

.dropdown-item:hover {
  background: #f8f9fa;
}

.dropdown-item:last-child {
  border-bottom: none;
}

/* Remove Ticket */
.remove-ticket {
  margin-top: 1rem;
  text-align: right;
}

.remove-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 0.9rem;
}

.remove-btn:hover {
  text-decoration: underline;
}

/* Add Ticket Section */
.add-ticket-section {
  margin-bottom: 2rem;
  text-align: center;
}

.add-ticket-btn {
  background: none;
  border: none;
  color: #333;
  font-size: 1rem;
  cursor: pointer;
  padding: 1rem;
  transition: color 0.3s ease;
}

.add-ticket-btn:hover {
  color: #007bff;
}

/* Continue Section */
.continue-section {
  margin-top: 3rem;
  padding-top: 2rem;
}

.continue-btn {
  padding: 1rem 3rem;
  border: 2px solid #007bff;
  border-radius: 8px;
  background: #fff;
  color: #007bff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
}

.continue-btn:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.15);
}

/* Responsive Design */
@media (max-width: 768px) {
  .ticket-details-content {
    padding: 0 1rem;
  }

  .ticket-row {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .continue-btn {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .ticket-details-content {
    padding: 0 0.5rem;
  }

  .ticket-item {
    margin-bottom: 1.5rem;
  }

  .ticket-dropdown, .ticket-input {
    padding: 0.8rem 0;
    font-size: 0.9rem;
  }

  .ticket-row {
    grid-template-columns: 1fr 1fr;
    gap: 0.8rem;
  }

  .dropdown-group.full-width {
    margin-top: 1rem;
  }

  .continue-btn {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }
}
</style>
